import React, { useState, useEffect } from "react";
import "./styles.scss";
import { SvgIcon } from "../../Shared";
import { getLocalizedString } from "../../../shared/translation";
import { ReportsForm } from "../../Tracking/Reports/Form";
import { getEcoDriveReportHeaders } from "./ecoDriveTableConfig";
import { usePagination } from "../../../hooks";
import { MPSharedHeader, MPSharedTable } from "../../ManagementPortal/Shared";
import { useGetEcoDriveReportsQuery, useGetAreasQuery } from "../services";

export const ReportsContainer = () => {
  const [showForm, setShowForm] = useState(false);

  // Applied filters (used for API calls)
  const [appliedFilters, setAppliedFilters] = useState({
    selectedArea: 1,
    dateRange: {
      start_dt: "20-11-2025",
      end_dt: "28-11-2025",
    },
  });

  // Form filters (temporary state before applying)
  const [formFilters, setFormFilters] = useState({
    selectedArea: 1,
    dateRange: {
      start_dt: "20-11-2025",
      end_dt: "28-11-2025",
    },
  });

  const { page, setPage, perPage } = usePagination();

  // Fetch eco drive reports using applied filters
  const {
    data: reportsData,
    isLoading: reportsLoading,
    error: reportsError,
    refetch: refetchReports,
  } = useGetEcoDriveReportsQuery({
    start_dt: appliedFilters.dateRange.start_dt,
    end_dt: appliedFilters.dateRange.end_dt,
    area_id: appliedFilters.selectedArea,
    page,
    per_page: perPage,
  });

  // Fetch areas for dropdown
  const { data: areasData, isLoading: areasLoading } = useGetAreasQuery({
    page: 1,
    per_page: 100,
  });

  const handleFormAreaChange = (areaId) => {
    setFormFilters((prev) => ({
      ...prev,
      selectedArea: areaId,
    }));
  };

  const handleFormDateChange = (field, value) => {
    setFormFilters((prev) => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        [field]: value,
      },
    }));
  };

  const applyFilters = () => {
    setAppliedFilters({ ...formFilters });
    setPage(1); // Reset to first page when applying filters
  };

  const resetFilters = () => {
    const defaultFilters = {
      selectedArea: 1,
      dateRange: {
        start_dt: "20-11-2025",
        end_dt: "28-11-2025",
      },
    };
    setFormFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setPage(1);
  };

  const refreshReports = () => {
    refetchReports();
  };

  const createNewHandler = () => {
    setShowForm(true);
  };

  const headers = getEcoDriveReportHeaders();

  // Extract reports array from API response
  const reports = reportsData?.data || reportsData || [];
  const totalCount = reportsData?.meta?.total || reports.length;

  return (
    <>
      <article className="main-container report-container">
        <MPSharedHeader
          resourceName="Report"
          createNewHandler={createNewHandler}
          groupName="Reports"
          addNewLabel={getLocalizedString("generate_report", "Generate Report")}
          heading={getLocalizedString("reports", "Reports")}
          defaultSearchType="simple"
          skipCurrentUserCheck
        />

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filter-group">
            <label htmlFor="area-select">{getLocalizedString("select_area", "Select Area")}:</label>
            <select
              id="area-select"
              value={formFilters.selectedArea}
              onChange={(e) => handleFormAreaChange(parseInt(e.target.value))}
              disabled={areasLoading || reportsLoading}
              className={areasLoading ? "loading" : ""}
            >
              {areasLoading ? (
                <option>{getLocalizedString("loading_areas", "Loading areas...")}</option>
              ) : (
                areasData?.data?.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))
              )}
            </select>
            {areasLoading && (
              <div className="input-loading-indicator">
                <span className="spinner" />
              </div>
            )}
          </div>

          <div className="filter-group">
            <label htmlFor="start-date">{getLocalizedString("start_date", "Start Date")}:</label>
            <input
              id="start-date"
              type="date"
              value={formFilters.dateRange.start_dt.split("-").reverse().join("-")}
              onChange={(e) => {
                const formattedDate = e.target.value.split("-").reverse().join("-");
                handleFormDateChange("start_dt", formattedDate);
              }}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="end-date">{getLocalizedString("end_date", "End Date")}:</label>
            <input
              id="end-date"
              type="date"
              value={formFilters.dateRange.end_dt.split("-").reverse().join("-")}
              onChange={(e) => {
                const formattedDate = e.target.value.split("-").reverse().join("-");
                handleFormDateChange("end_dt", formattedDate);
              }}
            />
          </div>

          <div className="filter-actions">
            <button
              className="btn btn-primary apply-btn"
              onClick={applyFilters}
              disabled={reportsLoading || areasLoading}
            >
              {reportsLoading ? (
                <span className="spinner" />
              ) : (
                <>{getLocalizedString("apply_filters", "Apply Filters")}</>
              )}
            </button>

            <button
              className="btn btn-outline reset-btn"
              onClick={resetFilters}
              disabled={reportsLoading || areasLoading}
            >
              {getLocalizedString("reset_filters", "Reset")}
            </button>
          </div>
        </div>

        {/* Error State */}
        {reportsError && (
          <div className="error-message">
            <SvgIcon name="error" wrapperClass="error-icon" />
            <p>
              {getLocalizedString("error_loading_reports", "Error loading reports")}:{" "}
              {reportsError?.data?.message || reportsError?.message || "Unknown error"}
            </p>
            <button className="btn btn-secondary" onClick={refreshReports}>
              {getLocalizedString("retry", "Retry")}
            </button>
          </div>
        )}

        {/* Reports Table */}
        <MPSharedTable
          resourceName="Eco Drive Report"
          isLoading={reportsLoading}
          error={reportsError}
          isSuccess={!reportsLoading && !reportsError}
          headers={headers}
          data={reports}
          pagination={{
            page,
            perPage,
            count: totalCount,
            setPage,
          }}
          className="report eco-drive-reports"
          auditKey=""
          skipCanDelete={true}
          skipClientId={true}
          skipCurrentUserCheck
        />
      </article>

      {showForm && <ReportsForm onClose={() => setShowForm(false)} />}
    </>
  );
};
